<template>
	<div class="vendors">
		<ul class="vendors__list">
			<li class="vendors-list__item" v-for="item in items" :key="item.id">
				<VendorsCard
					:modelValue="item"
					@showDependentModal="showDependentModal(item)"
					@showCustomerModal="showCustomerModal(item)"
				/>
			</li>
		</ul>
		<DependentVendorsModal
			:display="displayDependentVendorModal"
			:integratorId="integratorId"
			:integratorName="integratorName"
			v-if="displayDependentVendorModal"
			@close="closeDependentVendorModal"
		/>
		<CustomersModal
			:apiCall="customerResultApi"
			:display="displayCustomerModal"
			:successCustomerApi="successCustomerApi"
			:renderSource="true"
			:title="customerModalTitle"
			:loading="customerLoading"
			:placeholder="'Search clients, companies, and locations'"
			@customerSearch="setCustomerSearch"
			@close="cleanCustomerVariables"
		/>
	</div>
</template>

<script>
import VendorsCard from './vendors-summary-card';
import PaginationMixins from '@/mixins/paging';
import DependentVendorsModal from '../dependentModal/dependent-vendors-modal';
import CustomersModal from '@/sections/shared/customers-modal.vue';
import {
	MODULE_NAME as INTEGRATOR_MODULE,
	SET_DEALERSHIPCOMPANIES,
	DEALERSHIPCOMPANIES,
	SHOW_DEALERSHIPCOMPANIES_MODAL,
	SET_TOTAL_CUSTOMERS_COUNT,
	TOTAL_CUSTOMERS_COUNT
} from '../vendors-mutation-types';
import {
	GET_CUSTOMER_OPTION_NAME,
	GET_CUSTOMER_OPTION_IDS
} from '@/sections/options/options-mutation-type';
import CustomerOptionsMixins from '@/mixins/customers/customer-options';

//constants
import { mapState, mapActions, mapGetters } from 'vuex';

export default {
	name: 'VendorsSummaryList',
	mixins: [PaginationMixins, CustomerOptionsMixins],
	components: {
		VendorsCard,
		DependentVendorsModal,
		CustomersModal
	},
	props: {
		items: {
			type: Array,
			default: () => []
		}
	},
	data() {
		return {
			dataIntegrations: [],
			displayIntegratorsModal: false,
			displayDependentVendorModal: false,
			integratorId: null,
			integratorName: '',
			customerModalTitle: '',
			customerSearch: '',
			customers: [],
			customersParameter: { dealershipGroupIds: [] },
			displayCustomerModal: false,
			successCustomerApi: false,
			customerLoading: false
		};
	},
	computed: {
		...mapState('integrators', ['loading']),
		...mapGetters(INTEGRATOR_MODULE, [
			DEALERSHIPCOMPANIES,
			SHOW_DEALERSHIPCOMPANIES_MODAL,
			TOTAL_CUSTOMERS_COUNT
		])
	},
	methods: {
		...mapActions(INTEGRATOR_MODULE, [
			SET_DEALERSHIPCOMPANIES,
			SET_TOTAL_CUSTOMERS_COUNT
		]),
		showIntegrations(obj) {
			this.dataIntegrations = obj.integrations || [];
			this.displayIntegratorsModal = true;
		},
		showDependentModal({ id, integratorName }) {
			if (id) {
				this.integratorId = id;
				this.integratorName = integratorName;
				this.displayDependentVendorModal = true;
			} else {
				this.displayDependentVendorModal = false;
			}
		},
		closeDependentVendorModal() {
			this.$store.commit('integrators/setDependentsVendor', {});
			this.$store.commit('integrators/setLoadedDependents', false);
			this.displayDependentVendorModal = false;
		},
		showCustomerModal({ id, integratorName }) {
			this.integratorId = id;
			this.customerModalTitle = `${integratorName} CCL`;
			this.$store.dispatch(
				'integrators/showDealershipCompaniesModal',
				true
			);
		},
		async setCustomerSearch(value) {
			this.customerSearch = value;

			const getCustomerOptionIds = result => {
				if (!result || !result.length) return [];
				return this[GET_CUSTOMER_OPTION_IDS](result);
			};

			this.customersParameter.dealershipGroupIds = getCustomerOptionIds(
				value
			);
			await this.fetchCustomers();
		},
		formatCustomers() {
			let customers = this[DEALERSHIPCOMPANIES].items.map(cn => {
				let result = [];
				let customerName = this[GET_CUSTOMER_OPTION_NAME](
					cn.dealershipGuid
				);
				if (customerName) result.push(customerName);
				if (cn.displayName) result.push(cn.displayName);
				if (cn.locationName) result.push(cn.locationName);

				let customerCompanyLocation = result.join('-');
				return {
					text: customerCompanyLocation,
					value: customerCompanyLocation
				};
			});
			const cleanedCustomers = customers
				.filter(obj =>
					Object.values(obj).some(
						value =>
							value !== '' &&
							value !== null &&
							value !== undefined
					)
				)
				.map((obj, index) => ({ ...obj, key: `customer_${index}` }));
			return cleanedCustomers;
		},
		async fetchCustomers() {
			let query = { ...this.customersParameter };
			query.integratorId = this.integratorId;
			query.page = 1;
			query.count = 100;
			this.customers = [];

			this.customerLoading = true;

			if (this.customerSearch) {
				query.search = this.customerSearch;
			}
			this.successCustomerApi = false;
			await this[SET_DEALERSHIPCOMPANIES](query)
				.then(() => {
					this.customers = this.formatCustomers();
					if (this[DEALERSHIPCOMPANIES] && !this.customerSearch) {
						this.$store.dispatch(
							'integrators/totalCustomersCount',
							this[DEALERSHIPCOMPANIES].totalCount
						);
					}
					this.successCustomerApi = true;
				})
				.catch(e => {
					console.log(e);
				})
				.finally(() => {
					this.customerLoading = false;
				});
		},
		customerResultApi() {
			return this.customers;
		},
		cleanCustomerVariables() {
			this.displayCustomerModal = false;
			this.customerLoading = false;
			this.customers = [];
			this.customerSearch = '';
			this.customersParameter.dealershipGroupIds = [];
		}
	},
	watch: {
		[SHOW_DEALERSHIPCOMPANIES_MODAL]: {
			async handler(val) {
				if (val) {
					await this.fetchCustomers();
				}
				this.displayCustomerModal = val;
			}
		}
	}
};
</script>

<style lang="scss" scoped>
/**
	* Vendors Results List
	* Can use SCSS variables = TRUE
*/

.vendors {
	&__list {
		display: grid;
		grid-template-columns: 1fr;
		gap: $gutter-lg;

		@include mq($from: md) {
			grid-template-columns: 1fr 1fr;
		}

		@include mq($from: lg) {
			grid-template-columns: 1fr 1fr 1fr;
		}
	}
}
</style>
